import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
// MUI
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import TariffEditorDialog from './TariffEditorDialog'
import { currencySymbol } from '../../../../helpers/region'

// Quote/invoice defaults + tariff rate cards for PTS pricing
// (docs/product-specs/pts-competitive-roadmap.md §4).
const PTSPricingTariffs = () => {
  const [loading, setLoading] = useState(true)
  const [quoteValidityDays, setQuoteValidityDays] = useState(30)
  const [invoicePaymentTermsDays, setInvoicePaymentTermsDays] = useState(30)
  const [autoQuoteOnPortalBooking, setAutoQuoteOnPortalBooking] =
    useState(false)
  const [vatDefault, setVatDefault] = useState('standard')
  const [varianceFlagPct, setVarianceFlagPct] = useState(15)
  const [tariffs, setTariffs] = useState([])
  const [tariffsLoading, setTariffsLoading] = useState(true)
  const [editorTariff, setEditorTariff] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [archiveTariff, setArchiveTariff] = useState(null)
  const [archiving, setArchiving] = useState(false)

  const { setToast, tenantInfo } = useContext(GlobalContext)
  const currency = currencySymbol(tenantInfo?.region)

  const loadTariffs = () => {
    setTariffsLoading(true)
    API.get('/pts/tariffs')
      .then((res) => {
        if (res.status === 200) {
          setTariffs(res.data || [])
        } else {
          setToast({
            message: res?.data?.error || 'Unable to load tariffs',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({ message: 'Unable to load tariffs', severity: 'error' }),
      )
      .finally(() => setTariffsLoading(false))
  }

  useEffect(() => {
    loadTariffs()
  }, [])

  const setDefaultTariff = async (tariffID) => {
    const res = await API.post(`/pts/tariffs/${tariffID}/set-default`)
    if (res.status !== 200) {
      setToast({
        message: res?.data?.error || 'Unable to set the default tariff',
        severity: 'error',
      })
    }
    loadTariffs()
  }

  const confirmArchiveTariff = async () => {
    if (!archiveTariff?._id) return
    setArchiving(true)
    try {
      const res = await API.post(`/pts/tariffs/${archiveTariff._id}/archive`)
      if (res.status === 200) {
        setArchiveTariff(null)
        loadTariffs()
        setToast({
          message: `${archiveTariff.name} archived`,
          severity: 'success',
        })
      } else {
        setToast({
          message: res?.data?.error || 'Unable to archive tariff',
          severity: 'error',
        })
      }
    } catch (e) {
      // validateStatus means only network failures land here - no response body
      setToast({
        message: 'Unable to archive tariff',
        severity: 'error',
      })
    } finally {
      setArchiving(false)
    }
  }

  useEffect(() => {
    API.get('/settings?select=patientTransportSettings.pricing')
      .then((res) => {
        if (res.status === 200) {
          const pricing = res.data?.patientTransportSettings?.pricing || {}
          setQuoteValidityDays(pricing.quoteValidityDays ?? 30)
          setInvoicePaymentTermsDays(pricing.invoicePaymentTermsDays ?? 30)
          setAutoQuoteOnPortalBooking(!!pricing.autoQuoteOnPortalBooking)
          setVatDefault(pricing.vatDefault ?? 'standard')
          setVarianceFlagPct(pricing.varianceFlagPct ?? 15)
        } else {
          setToast({
            message: res?.data?.error || 'Unable to load pricing settings',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to load pricing settings',
          severity: 'error',
        }),
      )
      .finally(() => setLoading(false))
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    API.put('/site-config', {
      'patientTransportSettings.pricing.quoteValidityDays':
        Number(quoteValidityDays) || 30,
      'patientTransportSettings.pricing.invoicePaymentTermsDays':
        Number(invoicePaymentTermsDays) || 30,
      'patientTransportSettings.pricing.autoQuoteOnPortalBooking':
        autoQuoteOnPortalBooking,
      'patientTransportSettings.pricing.vatDefault': vatDefault,
      'patientTransportSettings.pricing.varianceFlagPct':
        Number(varianceFlagPct) || 15,
    }).then((res) => {
      if (res.status === 200) {
        setToast(true)
      } else {
        setToast({
          message: res?.data?.error || 'Unable to save pricing settings',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Box padding={1}>
      <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
        <Grid item xs={12}>
          <Typography variant="title2">PTS Pricing &amp; Tariffs</Typography>
        </Grid>
        {loading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
        <Grid item xs={12}>
          <Alert severity="info">
            <AlertTitle>Quote and invoice defaults</AlertTitle>
            These defaults apply to PTS quotes and invoices. Tariff rate cards
            (mileage, mobility uplifts, out-of-hours rates, and per-organisation
            contracts) are coming to this page next — they will price bookings
            automatically.
          </Alert>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            label="Quote validity (days)"
            type="number"
            fullWidth
            value={quoteValidityDays}
            onChange={(e) => setQuoteValidityDays(e.target.value)}
            inputProps={{ min: 1 }}
            helperText="Quotes expire this many days after sending"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            label="Invoice payment terms (days)"
            type="number"
            fullWidth
            value={invoicePaymentTermsDays}
            onChange={(e) => setInvoicePaymentTermsDays(e.target.value)}
            inputProps={{ min: 1 }}
            helperText="Default due date offset for new invoices"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <FormControl fullWidth>
            <InputLabel>Default VAT</InputLabel>
            <Select
              label="Default VAT"
              value={vatDefault}
              onChange={(e) => setVatDefault(e.target.value)}
            >
              <MenuItem value="standard">Standard rate</MenuItem>
              <MenuItem value="none">No VAT</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Box>
            <FormControlLabel
              label="Auto-quote portal bookings"
              control={
                <Switch
                  checked={autoQuoteOnPortalBooking}
                  onChange={(e) =>
                    setAutoQuoteOnPortalBooking(e.target.checked)
                  }
                />
              }
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info">
            The standard VAT rate follows your deployment region automatically
            (20% UK, 23% IE).
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title2" sx={{ mt: 1 }}>
            Billing runs &amp; Xero export
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            label="Variance flag threshold (%)"
            type="number"
            fullWidth
            value={varianceFlagPct}
            onChange={(e) => setVarianceFlagPct(e.target.value)}
            inputProps={{ min: 0 }}
            helperText="Flag actuals that differ from an accepted quote by more than this"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={9}>
          <Alert severity="info">
            Xero export account codes and tax-rate names are now configured
            under Company Settings &rarr; Accounting Export.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" fullWidth color="anGreen" type="submit">
            Submit
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            sx={{ mt: 2 }}
          >
            <Box>
              <Typography variant="title2">Tariffs</Typography>
              <Typography variant="body2" color="text.secondary">
                Rate cards that price quotes and invoices automatically. The
                default tariff applies to every organisation without a
                contract-specific one.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditorTariff(null)
                setEditorOpen(true)
              }}
            >
              New Tariff
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          {tariffs.length === 0 && !tariffsLoading ? (
            <Alert severity="warning">
              No tariffs yet — quotes and auto-priced invoices need at least
              one. Create your default rates to get started.
            </Alert>
          ) : (
            <CustomDataGrid
              rows={tariffs}
              loading={tariffsLoading}
              getRowId={(row) => row._id}
              autoHeight
              disableRowSelectionOnClick
              onRowClick={({ row }) => {
                setEditorTariff(row)
                setEditorOpen(true)
              }}
              slots={{ toolbar: () => null, footer: () => null }}
              columns={[
                { field: 'name', headerName: 'Tariff', flex: 1, minWidth: 160 },
                {
                  field: 'rules',
                  headerName: 'Rules',
                  width: 90,
                  valueGetter: (value, row) => row?.rules?.length || 0,
                },
                {
                  field: 'active',
                  headerName: 'Status',
                  width: 110,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.active === false ? 'Inactive' : 'Active'}
                      color={row.active === false ? 'default' : 'success'}
                      sx={{ borderRadius: 1 }}
                    />
                  ),
                },
                {
                  field: 'isDefault',
                  headerName: 'Default',
                  width: 160,
                  renderCell: ({ row }) =>
                    row.isDefault ? (
                      <Chip
                        size="small"
                        label="Default"
                        color="info"
                        sx={{ borderRadius: 1 }}
                      />
                    ) : row.active === false ? (
                      <Typography variant="caption" color="text.secondary">
                        Archived
                      </Typography>
                    ) : (
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          setDefaultTariff(row._id)
                        }}
                      >
                        Make default
                      </Button>
                    ),
                },
                {
                  field: 'actions',
                  headerName: '',
                  sortable: false,
                  filterable: false,
                  width: 70,
                  renderCell: ({ row }) =>
                    row.active === false ? null : (
                      <Tooltip title="Archive tariff">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={(event) => {
                            event.stopPropagation()
                            setArchiveTariff(row)
                          }}
                        >
                          <ArchiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                },
              ]}
            />
          )}
        </Grid>
      </Grid>
      <TariffEditorDialog
        open={editorOpen}
        tariff={editorTariff}
        currency={currency}
        onClose={() => setEditorOpen(false)}
        onSaved={loadTariffs}
      />
      <Dialog
        open={!!archiveTariff}
        onClose={archiving ? null : () => setArchiveTariff(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Archive tariff?</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <Typography variant="body2">
              Archive <strong>{archiveTariff?.name}</strong>? It will no longer
              be available for future PTS bookings or new organisation
              contracts.
            </Typography>
            {archiveTariff?.isDefault && (
              <Alert severity="warning">
                This is the default tariff. Archiving it will remove the default
                until another active tariff is made default.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setArchiveTariff(null)}
            color="inherit"
            disabled={archiving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmArchiveTariff}
            disabled={archiving}
          >
            {archiving ? 'Archiving...' : 'Archive Tariff'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PTSPricingTariffs
